import { useEffect, useRef } from 'react';
import { enterBootHumanWait } from '#~/routes/root/boot-budget.js';
import { useAuth0 } from '@auth0/auth0-react';
import { useShowSessionEnded } from '#~/notifications/session-ended.js';
import { useFeatureFlag } from '#~/posthog/hooks/use-feature-flag.js';
import {
  createAlertTracker,
  ALERT_ACTION,
  ALERT_ID
} from '#~/analytics/domains/alert-lifecycle.js';
import { track } from '#~/analytics/track.js';
import type { AnalyticsEvent } from '#~/analytics/types/index.js';
import { ElectronAPI } from '#~/electron/index.js';
import { isElectronApp } from '#~/electron/utils.js';
import {
  beginAuthJourney,
  buildHostedAuth0ExtParams,
  setActiveAuthJourney
} from './auth-journey.js';
import { authStore, useAuthStore } from './store.js';
import { writeSessionReturn } from './session-return.js';
import type { SessionReturn } from './session-return.js';

const alerts = createAlertTracker((name, category, properties) => {
  void track({ name, category, properties } as AnalyticsEvent);
});

/** Mounted in the existing Router, below the dialog provider and above auth guards. */
export const useSessionEnded = (): void => {
  const { enabled, loading } = useFeatureFlag('alert_n8_enabled', { defaultValue: true });
  const session = useAuthStore((state) => state.sessionEnded);
  const show = useShowSessionEnded();
  const { logout } = useAuth0();
  const shown = useRef<SessionReturn | undefined>(undefined);

  useEffect(() => {
    if (session) return enterBootHumanWait();
    return undefined;
  }, [session]);

  useEffect(() => {
    authStore.setState({ sessionEndedEnabled: !loading && enabled === true });
    return () => {
      authStore.setState({ sessionEndedEnabled: false });
    };
  }, [enabled, loading]);

  useEffect(() => {
    if (!session || session.phase !== 'prompt' || shown.current === session) return;
    shown.current = session;
    const studioId = /^\/room\/([^/]+)/u.exec(session.returnTo.split(/[?#]/u)[0]!)?.[1];
    const showing = {
      alert_id: ALERT_ID.N8,
      showing_id: '',
      ...(studioId ? { studio_id: studioId } : {})
    };
    showing.showing_id = show({
      onSignIn: () => {
        alerts.trackAlertActed(showing, { action: ALERT_ACTION.SIGN_IN });
        const next: SessionReturn = { ...session, phase: 'signing_in' };
        writeSessionReturn(next);
        if (isElectronApp()) {
          authStore.setState({ sessionEnded: next });
          const journey = beginAuthJourney('studio_login');
          setActiveAuthJourney(journey);
          ElectronAPI.login({
            type: 'email',
            authJourney: journey,
            hostedAuthParams: buildHostedAuth0ExtParams(journey)
          });
        } else {
          // Keep this document's guard paused until navigation. Resuming it
          // during SDK logout would start a competing hosted sign-in.
          // A fresh document drops the expired SDK error even if cleanup fails.
          const signIn = () => window.location.assign('/login');
          void logout({ openUrl: false }).then(signIn, signIn);
        }
      }
    });
    alerts.trackAlertShown(showing);
  }, [session, show, logout]);
};
